import { useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { getMomentTz } from "@dexteel/mesf-core";
import type { Moment } from "moment";
import { TaskDateSection } from "./TaskDateSection";

const moment = getMomentTz();

interface FormData {
  timestamp: Moment | null;
  operatorNumber: string;
  notes: string;
}

interface Props {
  open: boolean;
  title: string;
  confirmLabel: string;
  confirmColor: "primary" | "error" | "warning";
  /** PartId for loading task history in the DateTimePicker section */
  partId: number | null;
  /** Action type — controls timestamp validation rules */
  action: "uninstall" | "repair";
  onClose: () => void;
  onConfirm: (
    notes: string | null,
    operatorNumber: number,
    timestamp: Date | null,
  ) => void;
  /** Whether to validate against CASTER_EQP_INITIAL_DATETIME setting. Only for Caster mold module. */
  validateAgainstInitialDatetime?: boolean;
}

export const NotesModal = ({
  open,
  title,
  confirmLabel,
  confirmColor,
  partId,
  action,
  onClose,
  onConfirm,
  validateAgainstInitialDatetime = false,
}: Props) => {
  const { control, handleSubmit, reset, trigger } = useForm<FormData>({
    defaultValues: {
      timestamp: null,
      operatorNumber: "",
      notes: "",
    },
    mode: "onChange",
  });

  // Reset fields when modal opens
  useEffect(() => {
    if (open) {
      reset({
        timestamp: moment(),
        operatorNumber: "",
        notes: "",
      });
    }
  }, [open, reset]);

  const onSubmit = (data: FormData) => {
    onConfirm(
      data.notes.trim() || null,
      parseInt(data.operatorNumber, 10),
      data.timestamp?.toDate() ?? null,
    );
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          {/* Date selection with task history */}
          <TaskDateSection
            control={control}
            trigger={trigger}
            partId={partId}
            open={open}
            action={action}
            validateAgainstInitialDatetime={validateAgainstInitialDatetime}
          />

          <Controller
            name="notes"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Notes (optional)"
                fullWidth
                multiline
                rows={3}
                placeholder="Add any notes about this action..."
              />
            )}
          />

          <Controller
            name="operatorNumber"
            control={control}
            rules={{
              required: "Operator number is required",
            }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label="Operator Number"
                fullWidth
                size="small"
                error={!!error}
                helperText={error?.message || "Enter your Operator Number"}
                inputProps={{
                  inputMode: "numeric",
                }}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "");
                  field.onChange(value);
                }}
              />
            )}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          color={confirmColor}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
